import React from 'react';

import {
    Box,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Skeleton,
    Typography,
} from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import Icon, { SizeVariant } from '@apigrowfinance/pbolt-icon';
import { GenericCurrency } from '@apigrowfinance/pbolt-icons';
import { CustomTheme, useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';

import StatusTag from '@components/lending/repaymentsScheduleListItem/statusTag/StatusTag';
import { useAccount } from '@contexts/AccountProvider';
import { RepaymentStatus } from '@enums/lending/repaymentStatus';
import getEnumTranslation from '@utils/getEnumTranslation';

export interface RepaymentData {
    amountDue: number;
    amountPaid?: number;
    dateDue: string;
    period?: string;
    status: RepaymentStatus;
    repaymentId?: string;
}

export type RepaymentScheduleItemProps = {
    itemData: RepaymentData;
    isLoading?: boolean;
    showPeriod?: boolean;
    onPaper?: boolean;
    testId?: string;
    onClick?: () => void;
    iconSize?: SizeVariant;
};

const RepaymentScheduleListItem = ({
    itemData,
    isLoading = false,
    showPeriod = false,
    onPaper = false,
    testId,
    onClick,
    iconSize = SizeVariant.Medium,
}: RepaymentScheduleItemProps) => {
    const { theme } = useTheme();
    const { program } = useAccount();

    const listItem = (
        <Box {...(!!testId && { 'data-testid': testId })}>
            <ListItem
                disableGutters={false}
                sx={{
                    b: theme.spacing(1),
                    ...(onPaper && {
                        bgcolor: 'background.default',
                        borderRadius: 0.5,
                    }),
                }}
                secondaryAction={
                    <ListItemText
                        primaryTypographyProps={{
                            style: {
                                textAlign:
                                    theme.direction === 'rtl'
                                        ? 'left'
                                        : 'right',
                            },
                        }}
                        primary={
                            <Box data-testid="secondary-title">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                    }}>
                                    <StatusTag
                                        status={itemData?.status}
                                        dueDate={itemData?.dateDue}
                                        isLoading={isLoading}
                                    />
                                    {isLoading ? (
                                        <Skeleton
                                            width={120}
                                            height={25}
                                            data-testid="repayment-amount-loader"
                                        />
                                    ) : (
                                        <CurrencyFormatter
                                            amount={
                                                itemData?.amountPaid
                                                    ? itemData?.amountPaid
                                                    : itemData?.amountDue
                                            }
                                            currency={
                                                program?.currency_numeric_code
                                            }
                                            testId="repayment-amount"
                                        />
                                    )}
                                </Box>
                            </Box>
                        }
                        secondaryTypographyProps={{
                            style: {
                                textAlign:
                                    theme.direction === 'rtl'
                                        ? 'left'
                                        : 'right',
                            },
                        }}
                        secondary={
                            isLoading ? (
                                <Skeleton
                                    sx={{ float: 'right' }}
                                    width={35}
                                    data-testid="repayment-type-loader"
                                />
                            ) : (
                                <Box
                                    component="span"
                                    data-testid="repayment-type">
                                    {getEnumTranslation('debit')}
                                </Box>
                            )
                        }
                    />
                }>
                <ListItemAvatar sx={{ mr: 2.25 }}>
                    <Icon data-testid="repayment-icon" sizeVariant={iconSize}>
                        {isLoading ? (
                            <Skeleton
                                width={60}
                                height={60}
                                variant="circular"
                                data-testid="repayment-avatar-loader"
                            />
                        ) : (
                            <GenericCurrency fontSize="inherit" />
                        )}
                    </Icon>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        isLoading ? (
                            <Skeleton
                                width={90}
                                height={25}
                                data-testid="repayment-text-loader"
                            />
                        ) : (
                            <Typography
                                variant="h6"
                                data-testid="primary-title"
                                sx={(theme: CustomTheme) => ({
                                    color:
                                        theme?.custom?.brand?.textMain ||
                                        'primary.main',
                                })}>
                                <FormattedMessage
                                    id="RepaymentsSchedulePanel.loanPayment"
                                    defaultMessage="Loan Payment"
                                />
                            </Typography>
                        )
                    }
                    secondary={
                        isLoading ? (
                            <Skeleton
                                width={70}
                                data-testid="repayment-date-loader"
                            />
                        ) : (
                            <>
                                <DateFormatter date={itemData?.dateDue} />
                                {showPeriod && (
                                    <Box component="span">
                                        {' • '}
                                        <FormattedMessage
                                            defaultMessage="Period {period}"
                                            id="RepaymentsScheduleListItem.period"
                                            values={{
                                                period: itemData?.period,
                                            }}
                                        />
                                    </Box>
                                )}
                            </>
                        )
                    }
                />
            </ListItem>
        </Box>
    );
    return onClick != null ? (
        <ListItemButton
            disabled={isLoading}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                alignItems: 'normal',
            }}
            onClick={onClick}>
            {listItem}
        </ListItemButton>
    ) : (
        listItem
    );
};

export default RepaymentScheduleListItem;
